import * as React from 'react';
import {DefaultParams, useLocation, useRoute} from 'wouter';

import {useDocumentHistoryQuery} from './document-history-query';
import {DocRevisionsContext} from './doc-revisions-context';

interface DocRouteParams extends DefaultParams {
    documentId: string;
    revisionId?: string;
}

export function DocRevisionsContextProvider(props: {children: React.ReactNode}) {
    const [, navigate] = useLocation();

    const [isNewDocRoute] = useRoute('/documents/edit/new/:type');

    const [isEditRevisions, editRevisionsParams] = useRoute<DocRouteParams>(
        '/documents/edit/:documentId/revisions/:revisionId?/*?'
    );
    const [isExploreRevisions, exploreRevisionsParams] = useRoute<DocRouteParams>(
        '/documents/explore/:documentId/revisions/:revisionId?/*?'
    );
    const [, editParams] = useRoute<DocRouteParams>('/documents/edit/:documentId/*?');
    const [, exploreParams] = useRoute<DocRouteParams>('/documents/explore/:documentId/*?');

    const documentsViewMode = isEditRevisions || editParams ? 'edit' : 'explore';
    const isHistoryVisible = isEditRevisions || isExploreRevisions;
    const revisionId = editRevisionsParams?.revisionId ?? exploreRevisionsParams?.revisionId;
    const documentId = isNewDocRoute
        ? undefined
        : (editParams?.documentId ?? exploreParams?.documentId);

    const documentHistoryQuery = useDocumentHistoryQuery(documentId);

    React.useEffect(() => {
        if (isHistoryVisible && !revisionId && documentHistoryQuery.data?.[0]) {
            navigate(
                `/documents/${documentsViewMode}/${documentId}/revisions/${documentHistoryQuery.data?.[0].revision}`,
                {replace: true}
            );

            documentHistoryQuery.refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isHistoryVisible, revisionId, documentHistoryQuery.data]);

    const selectedRevision = React.useMemo(() => {
        return documentHistoryQuery.data && revisionId
            ? documentHistoryQuery.data.find((item) => item.revision === revisionId)
            : undefined;
    }, [revisionId, documentHistoryQuery.data]);

    const value = {
        selectedRevision,
        revisions: documentHistoryQuery.data || null
    };

    return (
        <DocRevisionsContext.Provider value={value}>{props.children}</DocRevisionsContext.Provider>
    );
}
